import { Brand, Node, NodeDataFolder, NodeDataFolderResponse, Tree } from "@org-avp/avp-avengers-template-service-api";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback } from "react";
import { templateApi } from "../apis/template-service-api";
import { createCachedLookup } from "../utils/lookup.utils";
import { traverse } from "../utils/tree.utils";

const pollingIntervalInMS = 5_000;

export interface TemplateHelper {
  templateTreeResponse: {
    data?: Tree;
    isLoading: boolean;
    error?: unknown;
  };

  nodeDataFoldersResponse: {
    data?: NodeDataFolderResponse;
    isLoading: boolean;
    error?: unknown;
  };

  createNodeDataFolderLookup(): Map<string, NodeDataFolder>;

  removeNodesFromTree(nodeUUIDs: string[]): void;

  removeNodesResult: {
    isLoading: boolean;
    error?: unknown;
  };
}

export const useTemplateHelper = (brand: Brand, templateId: string): TemplateHelper => {
  const templateTreeResponse = templateApi.useGetTreeQuery([brand, templateId], { pollingInterval: pollingIntervalInMS });

  const nodeDataFoldersResponse = templateApi.useGetFoldersQuery([brand, templateId]);
  templateApi.useGetUpdatedFoldersQuery(
    nodeDataFoldersResponse.data ? [brand, templateId, nodeDataFoldersResponse.data.lastModified] : skipToken,
    { pollingInterval: pollingIntervalInMS },
  );

  const [updateTreeMutation, removeNodesResult] = templateApi.useUpdateTreeMutation();
  const removeNodesFromTree = useCallback(
    (nodeUUIDs: string[]) => {
      const nodeFilter = (it: Node) => !nodeUUIDs.includes(it.uuid);
      const treeCopy: Tree = JSON.parse(JSON.stringify(templateTreeResponse.data));
      treeCopy.children = treeCopy.children.filter(nodeFilter);
      const filterChildren = (node: Node) => node.children = node.children.filter(nodeFilter);
      traverse(treeCopy.children, filterChildren);

      updateTreeMutation([brand, templateId, treeCopy, true]);
    },
    [brand, templateId, templateTreeResponse.data, updateTreeMutation],
  );

  return {
    templateTreeResponse,
    nodeDataFoldersResponse,
    createNodeDataFolderLookup: useCallback(
      () => cacheNodeDataFolderLookup(nodeDataFoldersResponse.data),
      [nodeDataFoldersResponse.data],
    ),
    removeNodesFromTree,
    removeNodesResult,
  };
};

const nodeDataFolderLookupCache = new WeakMap<NodeDataFolderResponse, Map<string, NodeDataFolder>>();
const cacheNodeDataFolderLookup = (nodeDataFolders: NodeDataFolderResponse | undefined) => {
  return createCachedLookup(
    nodeDataFolderLookupCache,
    nodeDataFolders,
    (it) => it.content,
    (it) => it.nodeUUID,
  );
};
